import { useEffect, useState, useCallback } from "react";
import { Loader2, EyeOff, Eye, UserPlus, ChevronDown, ChevronRight, Smartphone, Globe, Check, X, Activity, Trash2, Users, ShoppingBag, TrendingUp, TriangleAlert } from "lucide-react";
import { getRegistrations, getAdminSlipOverview, purgeAllHistory, getCommunityDeals, reviewCommunityDeal, getAdminDisabledLinks, reactivateDisabledLink } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import PushBroadcastCard from "@/components/PushBroadcastCard";

const card = "rounded-2xl border border-gray-200 bg-white shadow-sm";
const sectionTitle = "flex items-center gap-2 text-sm font-bold uppercase tracking-[0.1em] text-gray-900";

const IGNORE_KEY = "cheapify_admin_ignore";

// Owner-exclusion: when ON, this device's activity is flagged internal so it never
// inflates public metrics. (The only tracking config retained after the v1.6.0 strip-down.)
function IgnoreActivityToggle() {
  const [ignore, setIgnore] = useState(false);

  useEffect(() => {
    try { setIgnore(localStorage.getItem(IGNORE_KEY) === "true"); } catch (e) { console.error("read admin-ignore pref failed (ignored):", e); }
  }, []);

  const toggle = () => {
    const next = !ignore;
    try {
      if (next) localStorage.setItem(IGNORE_KEY, "true");
      else localStorage.removeItem(IGNORE_KEY);
    } catch (e) { console.error("write admin-ignore pref failed (ignored):", e); }
    setIgnore(next);
    toast.success(next ? "Your activity is now hidden from public metrics." : "Your activity is now counted again.");
  };

  return (
    <div className={`${card} flex items-center gap-4 p-5`} data-testid="ignore-activity-card">
      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${ignore ? "bg-gray-900" : "bg-gradient-to-br from-brand-yellow to-brand-orange"}`}>
        {ignore ? <EyeOff className="h-5 w-5 text-brand-yellow" /> : <Eye className="h-5 w-5 text-black" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold">Ignore My Activity</p>
        <p className="text-xs text-gray-500">{ignore ? "ON — your testing is excluded from the metrics below." : "OFF — your activity counts toward public metrics."}</p>
      </div>
      <button
        onClick={toggle}
        data-testid="ignore-activity-toggle"
        role="switch"
        aria-checked={ignore}
        className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${ignore ? "bg-brand-orange" : "bg-gray-300"}`}
      >
        <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${ignore ? "left-6" : "left-1"}`} />
      </button>
    </div>
  );
}

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

// One collapsible signup-source folder.
function RegFolder({ id, icon: Icon, label, hint, bucket, eventInfo }) {
  const [open, setOpen] = useState(false);
  const cap = eventInfo?.target_cap || 0;
  const live = eventInfo?.count ?? 0;
  const pct = cap ? Math.min(100, Math.round((live / cap) * 100)) : 0;
  // The Event folder represents the ACTIVE-EVENT MILESTONE (total counted members),
  // so its header + list mirror the milestone. This fixes the confusing mismatch where
  // the header read "0" (source bucket) while the tracker inside read "7/50".
  const isEvent = !!eventInfo;
  const rows = isEvent ? (eventInfo.signups || []) : (bucket?.rows || []);
  const count = isEvent ? live : (bucket?.count ?? 0);
  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden" data-testid={`reg-folder-${id}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`reg-folder-toggle-${id}`}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50"
      >
        {open ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
        <Icon className="h-4 w-4 text-brand-orange" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-gray-900">{label}</p>
          <p className="text-[11px] text-gray-400">{hint}</p>
        </div>
        <span className="shrink-0 text-lg font-bold tracking-tighter text-gray-900" data-testid={`reg-count-${id}`}>{isEvent ? `${live}/${cap || "—"}` : count}</span>
      </button>
      {open && (
        <div className="border-t border-gray-100 bg-gray-50" data-testid={`reg-list-${id}`}>
          {eventInfo && (
            <div className="border-b border-gray-100 bg-amber-50 px-5 py-3" data-testid="active-event-tracker">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-amber-700">
                  Active Event {eventInfo.active ? "· LIVE" : "· Off"}
                </span>
                <span className="text-sm font-bold text-amber-800" data-testid="active-event-progress">{live} / {cap || "—"}</span>
              </div>
              {cap > 0 && (
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-amber-200">
                  <div className="h-full rounded-full bg-gradient-to-r from-brand-yellow to-brand-orange" style={{ width: `${pct}%` }} />
                </div>
              )}
              {eventInfo.winner && (
                <p className="mt-2 text-xs font-bold text-green-700" data-testid="active-event-winner">🎉 Winner: {eventInfo.winner.email} · {eventInfo.winner.amount} credits</p>
              )}
            </div>
          )}
          {rows.length === 0 ? (
            <p className="px-5 py-4 text-xs text-gray-400" data-testid={`reg-empty-${id}`}>No sign-ups in this category yet.</p>
          ) : (
            <ul className="max-h-56 overflow-auto">
              {rows.map((r, i) => (
                <li key={`${r.email}-${i}`} data-testid={`reg-row-${id}-${i}`} className="flex items-center justify-between gap-4 border-b border-gray-100 px-5 py-2.5 last:border-b-0">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-gray-900">{r.name || "—"}</p>
                    <p className="truncate text-xs text-gray-500">{r.email}</p>
                  </div>
                  <span className="shrink-0 text-xs text-gray-400">{fmt(r.joined)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function TotalRegistrations({ data }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`${card} overflow-hidden`} data-testid="total-registrations-card">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="total-registrations-toggle"
        className="flex w-full items-center gap-3 px-6 py-5 text-left transition-colors hover:bg-gray-50"
      >
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand-yellow to-brand-orange">
          <UserPlus className="h-5 w-5 text-black" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold uppercase tracking-[0.1em] text-gray-900">Customer Email Tracking</p>
          <p className="text-xs text-gray-500">Tap to {open ? "hide" : "view"} the sign-up sources</p>
        </div>
        <span className="shrink-0 text-3xl font-bold tracking-tighter text-gray-900" data-testid="total-registrations-count">{data?.total ?? 0}</span>
        {open ? <ChevronDown className="h-5 w-5 text-gray-400" /> : <ChevronRight className="h-5 w-5 text-gray-400" />}
      </button>
      {open && (
        <div className="space-y-3 border-t border-gray-100 p-4" data-testid="registrations-folders">
          <RegFolder id="pwa" icon={Smartphone} label="PWA Sign-ups" hint="Registered inside the installed app" bucket={data?.pwa} />
          <RegFolder id="website" icon={Globe} label="Website Sign-ups" hint="All other web browser registrations" bucket={data?.website} />
        </div>
      )}
    </div>
  );
}

// Owner Analytics Dashboard V1 — live Slip metrics only (search analytics fully retired).
function SlipMetrics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAdminSlipOverview().then(setData).catch((e) => console.error("slip overview load failed:", e)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className={`${card} flex items-center gap-2 p-5 text-sm text-gray-500`}><Loader2 className="h-4 w-4 animate-spin" /> Loading Slip metrics…</div>;
  if (!data) return null;

  const subs = data.subscriptions?.distribution || {};
  const conv = data.purchase_conversion || {};
  const snag = data.snaggedify_metrics || {};
  const stat = (label, val) => (
    <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-center">
      <p className="text-2xl font-black tracking-tighter text-gray-900">{val}</p>
      <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-500">{label}</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-slip-metrics">
      {/* Subscription tiers */}
      <div className={`${card} p-6`} data-testid="admin-subscription-tiers">
        <h3 className={sectionTitle}><Users className="h-4 w-4 text-brand-orange" /> Subscription Revenue &amp; Tiers</h3>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {["free", "plus", "pro", "gold"].map((p) => (
            <div key={p} className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-center" data-testid={`tier-count-${p}`}>
              <p className="text-2xl font-black tracking-tighter text-gray-900">{subs[p] ?? 0}</p>
              <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-500">{p}</p>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-gray-500" data-testid="tier-total">Total tracked users — <span className="font-bold text-gray-900">{data.subscriptions?.total_users ?? 0}</span></p>
      </div>

      {/* Purchase conversion */}
      <div className={`${card} p-6`} data-testid="admin-purchase-conversion">
        <h3 className={sectionTitle}><TrendingUp className="h-4 w-4 text-brand-orange" /> Purchase Conversion (re-entry check)</h3>
        <div className="mt-4 grid grid-cols-3 gap-3">
          {stat("Yes, I Bought It", conv.confirmed_purchases ?? 0)}
          {stat("No, Just Browsing", conv.browsing_only ?? 0)}
          {stat("Conversion Rate", `${conv.conversion_rate_pct ?? 0}%`)}
        </div>
      </div>

      {/* Pack & Card Activity and Algorithm Performance widgets REMOVED (legacy
          pack-opening system is retired from the active Snaggedify shell) — replaced
          below by real e-commerce metrics. */}

      {/* Snaggedify real revenue/saves/affiliate metrics */}
      <div className={`${card} p-6`} data-testid="admin-snaggedify-metrics">
        <h3 className={sectionTitle}><ShoppingBag className="h-4 w-4 text-brand-orange" /> E-Commerce Metrics</h3>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {stat("Deals Checked", snag.total_checks ?? 0)}
          {stat("Affiliate Clicks", snag.affiliate?.total_clicks ?? 0)}
          {stat("Vault Saves", snag.total_saves ?? 0)}
          {stat("Est. MRR", `$${(snag.revenue?.estimated_mrr ?? 0).toFixed(2)}`)}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          {stat("Total Revenue (all-time)", `$${(snag.revenue?.total ?? 0).toFixed(2)}`)}
          {stat("Est. Affiliate Earnings", `$${(snag.affiliate?.estimated_earnings ?? 0).toFixed(2)}`)}
        </div>
        {(snag.affiliate?.top_merchants || []).length > 0 && (
          <div className="mt-4" data-testid="admin-top-merchants">
            <p className="text-[10px] font-black uppercase tracking-[0.12em] text-gray-400">Top Merchants by Clicks</p>
            <ul className="mt-2 space-y-1">
              {snag.affiliate.top_merchants.map((m) => (
                <li key={m.merchant} data-testid={`top-merchant-${m.merchant}`} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-1.5 text-sm">
                  <span className="font-bold text-gray-800">{m.merchant}</span>
                  <span className="font-bold text-gray-500">{m.clicks} clicks</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

// Link Availability Engine — admin review log of archived (broken/out-of-stock)
// product links, with a one-tap manual reactivate per the owner's review flow.
function DisabledLinksCard() {
  const [links, setLinks] = useState(null);
  const [busyUrl, setBusyUrl] = useState(null);

  const refresh = useCallback(() => {
    getAdminDisabledLinks().then((r) => setLinks(r.links || [])).catch(() => setLinks([]));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const reactivate = async (url) => {
    setBusyUrl(url);
    try {
      await reactivateDisabledLink(url);
      setLinks((prev) => prev.filter((l) => l.url !== url));
      toast.success("Link reactivated.");
    } catch (e) {
      toast.error(e.message || "Reactivate failed.");
    } finally {
      setBusyUrl(null);
    }
  };

  return (
    <div className={`${card} overflow-hidden`} data-testid="disabled-links-card">
      <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
        <TriangleAlert className="h-4 w-4 text-brand-orange" />
        <h3 className="text-sm font-bold uppercase tracking-[0.1em]">Disabled Product Links</h3>
        <span className="ml-auto rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-bold text-gray-600" data-testid="disabled-links-count">{links?.length ?? 0}</span>
      </div>
      {links === null ? (
        <div className="flex items-center gap-2 px-6 py-8 text-sm text-gray-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : links.length === 0 ? (
        <p className="px-6 py-8 text-sm text-gray-400" data-testid="disabled-links-empty">No archived links — every tracked product is live.</p>
      ) : (
        <ul>
          {links.map((l, i) => (
            <li key={l.url} data-testid={`disabled-link-${i}`} className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-3 last:border-b-0">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-gray-900">{l.title || l.domain || "Unknown product"}</p>
                <a href={l.url} target="_blank" rel="noopener noreferrer" className="block truncate text-xs text-brand-orange underline">{l.url}</a>
                <p className="mt-0.5 text-xs text-gray-500">{l.availability === "out_of_stock" ? "Out of Stock" : "Broken / Expired"} · {l.reason || "unavailable"} · Checked {fmt(l.last_checked_at)}</p>
              </div>
              <button
                onClick={() => reactivate(l.url)}
                disabled={busyUrl === l.url}
                data-testid={`disabled-link-reactivate-${i}`}
                className="shrink-0 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-600 hover:bg-emerald-100 disabled:opacity-50"
              >
                {busyUrl === l.url ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Reactivate"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


export default function AdminDashboard() {
  const [registrations, setRegistrations] = useState(null);
  const [communityDeals, setCommunityDeals] = useState([]);

  const refresh = useCallback(async () => {
    const [r, c] = await Promise.allSettled([getRegistrations(), getCommunityDeals()]);
    if (r.status === "fulfilled") setRegistrations(r.value || null);
    if (c.status === "fulfilled") setCommunityDeals(c.value.deals || []);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const reviewDeal = async (dealId, status) => {
    setCommunityDeals((d) => d.filter((x) => x.deal_id !== dealId));
    try {
      await reviewCommunityDeal(dealId, status);
      toast.success(status === "approved" ? "Deal approved." : "Deal rejected.");
    } catch (err) {
      toast.error(err.message || "Action failed.");
      refresh();
    }
  };

  return (
    <div className="mt-8 space-y-6" data-testid="admin-dashboard">
      <IgnoreActivityToggle />

      <TotalRegistrations data={registrations} />

      <SlipMetrics />

      <DisabledLinksCard />

      {/* System Health */}
      <div className={`${card} p-6`} data-testid="admin-system-health">
        <h3 className={sectionTitle}><Activity className="h-4 w-4 text-brand-orange" /> System Health</h3>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {[["API", "Operational"], ["Database", "Operational"], ["Search", "Operational"]].map(([svc, st]) => (
            <div key={svc} className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3" data-testid={`health-${svc.toLowerCase()}`}>
              <div className="flex items-center gap-2">
                <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                <p className="text-xs font-bold text-gray-900">{svc}</p>
              </div>
              <p className="mt-1 text-[11px] font-semibold text-emerald-600">{st}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Community deal submissions — pending "Submit Alternative Website" queue */}
      <div className={`${card} overflow-hidden`} data-testid="community-deals-card">
        <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
          <Globe className="h-4 w-4 text-brand-orange" />
          <h3 className="text-sm font-bold uppercase tracking-[0.1em]">Community Deal Submissions</h3>
          <span className="ml-auto rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-bold text-gray-600" data-testid="community-deals-count">{communityDeals.length}</span>
        </div>
        {communityDeals.length === 0 ? (
          <p className="px-6 py-8 text-sm text-gray-400" data-testid="community-deals-empty">No pending submissions.</p>
        ) : (
          <ul>
            {communityDeals.map((d) => (
              <li key={d.deal_id} data-testid={`community-deal-${d.deal_id}`} className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-3 last:border-b-0">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-gray-900">{d.item_name}</p>
                  <a href={d.website_url} target="_blank" rel="noopener noreferrer" className="block truncate text-xs text-brand-orange underline">{d.website_url}</a>
                  {d.note && <p className="mt-0.5 truncate text-xs text-gray-500">{d.note}</p>}
                  <p className="mt-0.5 text-[11px] text-gray-400">by {d.submitted_by || "—"} · {fmt(d.submitted_at)}</p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button onClick={() => reviewDeal(d.deal_id, "approved")} data-testid={`community-approve-${d.deal_id}`} aria-label="Approve" className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-emerald-600 hover:bg-emerald-100">
                    <Check className="h-4 w-4" />
                  </button>
                  <button onClick={() => reviewDeal(d.deal_id, "rejected")} data-testid={`community-reject-${d.deal_id}`} aria-label="Reject" className="rounded-lg border border-red-200 bg-red-50 p-2 text-red-500 hover:bg-red-100">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Send Push Notification to all subscribed members */}
      <PushBroadcastCard />

      {/* Maintenance: truncate all users' search history */}
      <div className={`${card} p-5`} data-testid="admin-purge-history-card">
        <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.1em] text-gray-900"><Trash2 className="h-4 w-4 text-brand-orange" /> Search History Maintenance</h3>
        <p className="mt-2 text-xs text-gray-500">Wipe ALL users' search history to a blank slate. Use only to clear legacy records. This cannot be undone.</p>
        <button
          onClick={async () => {
            if (!window.confirm("Clear ALL search history for EVERY user? This cannot be undone.")) return;
            try { const r = await purgeAllHistory(); toast.success(`Cleared ${r.deleted} history records.`); }
            catch (e) { toast.error(e.message || "Purge failed."); }
          }}
          data-testid="purge-history-btn"
          className="mt-3 inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 h-10 text-sm font-bold text-red-600 transition-colors hover:bg-red-100"
        >
          🗑 Clear All History
        </button>
      </div>
    </div>
  );
}
